/**
 * designStore — local persistence for design specs in the sandbox UI.
 *
 * Specs are kept as `id → DesignSpec` and mirrored to localStorage so
 * they survive page reloads. The agent-side write tools target a
 * `Map<string, string>` of sandbox files; when wiring is hooked up,
 * the orchestrator will translate between this store's record format
 * and the on-disk markdown form via `parseSpec` / `serializeSpec`.
 *
 * The store is a tiny event-emitting container — components subscribe
 * via `useDesignStore` (a hook) and re-render on change. We avoid
 * pulling in Zustand / Redux for one screen.
 */

import { useSyncExternalStore } from 'react';
import { generateSpecId, parseSpec, serializeSpec } from './parseSpec';
import type { DesignSpec } from './types';
import { DEFAULT_SEED_SPECS } from './seed';

// ─── Storage ─────────────────────────────────────────────────────────

const STORAGE_KEY = 'gotti.sandbox.designSpecs.v2';

interface DesignStoreState {
  /** id → spec, always sorted by updatedAt desc when iterated. */
  specs: Map<string, DesignSpec>;
  /** Monotonic version stamp; bumped on every mutation so React re-renders. */
  version: number;
}

let state: DesignStoreState = {
  specs: new Map(),
  version: 0,
};

const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

// ─── Persistence ─────────────────────────────────────────────────────

function loadFromStorage(): Map<string, DesignSpec> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { specs: Array<[string, string]> } | null;
    if (!parsed?.specs) return null;
    const out = new Map<string, DesignSpec>();
    // Each entry is [id, serializedMarkdown] — re-parse so we get a
    // single canonical form. If anything has rotted, it's silently
    // dropped instead of crashing the panel.
    for (const [id, content] of parsed.specs) {
      const r = parseSpec(content);
      if (r.ok && r.spec.id === id) out.set(id, r.spec);
    }
    return out;
  } catch {
    return null;
  }
}

function persistToStorage(specs: Map<string, DesignSpec>): void {
  try {
    const entries = [...specs.values()].map(spec => [spec.id, serializeSpec(spec)] as const);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ specs: entries }));
  } catch {
    // localStorage may be disabled (private mode). Ignore — the panel
    // still works in-memory for the session.
  }
}

// Initialize: load existing specs OR start empty.
// NOTE: design specs are about to move into GboxDocument (`design` field) and
// out of localStorage entirely. Until that lands, this init does NOT seed
// anything globally — fresh sandboxes start with an empty design panel.
// Also actively purges the legacy v1 key so leftover snake-themed content
// from older builds can't haunt anyone's browser.
{
  try { localStorage.removeItem('gotti.sandbox.designSpecs.v1'); } catch { /* private mode */ }
  const fromStorage = loadFromStorage();
  if (fromStorage && fromStorage.size > 0) {
    state = { specs: fromStorage, version: 1 };
  } else {
    state = { specs: new Map(), version: 1 };
  }
  if (typeof console !== 'undefined') {
    console.info('[designStore] init: specs =', state.specs.size, 'storage key =', STORAGE_KEY);
  }
}

// ─── Public API ──────────────────────────────────────────────────────

/** Returns the current state (do NOT mutate). */
export function getState(): DesignStoreState {
  return state;
}

/** Subscribe to mutations; returns an unsubscribe fn. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Get all live specs sorted by updated-at descending. */
export function listSpecs(): DesignSpec[] {
  return [...state.specs.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** Get a single spec by ID. */
export function getSpec(id: string): DesignSpec | undefined {
  return state.specs.get(id);
}

/** Create a new spec; returns the assigned id. */
export function createSpec(input: Omit<DesignSpec, 'id' | 'createdAt' | 'updatedAt'>): DesignSpec {
  const id = generateSpecId(candidate => state.specs.has(candidate));
  const now = new Date().toISOString();
  const spec: DesignSpec = {
    ...input,
    id,
    createdAt: now,
    updatedAt: now,
  };
  setSpec(spec);
  return spec;
}

/** Insert or update a spec. */
export function setSpec(spec: DesignSpec): void {
  const next = new Map(state.specs);
  next.set(spec.id, spec);
  state = { specs: next, version: state.version + 1 };
  persistToStorage(next);
  notify();
}

/** Update a spec by id; merges fields and bumps `updatedAt`. */
export function updateSpec(id: string, patch: Partial<Omit<DesignSpec, 'id' | 'createdAt'>>): DesignSpec | null {
  const existing = state.specs.get(id);
  if (!existing) return null;
  const updated: DesignSpec = {
    ...existing,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  setSpec(updated);
  return updated;
}

/** Delete a spec entirely. (Distinct from archive — used only for true mistakes.) */
export function deleteSpec(id: string): void {
  if (!state.specs.has(id)) return;
  const next = new Map(state.specs);
  next.delete(id);
  state = { specs: next, version: state.version + 1 };
  persistToStorage(next);
  notify();
}

/** Replace the whole store (e.g. after import). */
export function replaceAll(specs: DesignSpec[]): void {
  const next = new Map<string, DesignSpec>();
  for (const s of specs) next.set(s.id, s);
  state = { specs: next, version: state.version + 1 };
  persistToStorage(next);
  notify();
}

/** Clear everything and re-seed (test / demo helper). */
export function resetToSeed(): void {
  replaceAll(DEFAULT_SEED_SPECS);
}

// ─── React hook ──────────────────────────────────────────────────────

/**
 * Subscribe a component to the store. Returns the live spec list.
 * `useSyncExternalStore` is the recommended primitive for tearing-free
 * subscription in React 18+.
 */
export function useDesignSpecs(): DesignSpec[] {
  const version = useSyncExternalStore(
    subscribe,
    () => state.version,
    () => 0,
  );
  // Reading `version` in the snapshot ensures React re-runs this hook
  // when state changes; returning the sorted list each time is fine
  // for the panel's scale (~hundreds of specs at most).
  void version;
  return listSpecs();
}
